import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";

// Landing page (eager – it's the entry point)
import LandingPageV3 from "./pages/LandingPageV3";

// Auth pages – lazy
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./pages/auth/SignupPage"));

// App layout & pages – lazy
const AppLayout = lazy(() => import("./components/layout/AppLayout"));
const DashboardPage = lazy(() => import("./pages/app/DashboardPage"));
const SocialCalendarPage = lazy(() => import("./pages/app/SocialCalendarPage"));
const NewsletterCalendarPage = lazy(() => import("./pages/app/NewsletterCalendarPage"));
const TopicsPage = lazy(() => import("./pages/app/TopicsPage"));
const ContentListPage = lazy(() => import("./pages/app/ContentListPage"));
const ContentEditorPage = lazy(() => import("./pages/app/ContentEditorPage"));
const AssetsPage = lazy(() => import("./pages/app/AssetsPage"));
const SubscribersPage = lazy(() => import("./pages/app/SubscribersPage"));
const IntegrationsPage = lazy(() => import("./pages/app/IntegrationsPage"));
const OAuthCallbackPage = lazy(() => import("./pages/app/OAuthCallbackPage"));
const TeamPage = lazy(() => import("./pages/app/TeamPage"));
const NewsletterEditorPage = lazy(() => import("./pages/app/NewsletterEditorPage"));
const OnboardingPage = lazy(() => import("./pages/app/OnboardingPage"));
const CompanyProfilePage = lazy(() => import("./pages/app/CompanyProfilePage"));
const AccountSettingsPage = lazy(() => import("./pages/app/AccountSettingsPage"));
const PublishQueuePage = lazy(() => import("./pages/app/PublishQueuePage"));
const PublishHistoryPage = lazy(() => import("./pages/app/PublishHistoryPage"));

// V3 sub-pages – lazy
const PriserPage = lazy(() => import("./pages/v3/PriserPage"));
const FunktionerPage = lazy(() => import("./pages/v3/FunktionerPage"));
const SaFunkarDetPage = lazy(() => import("./pages/v3/SaFunkarDetPage"));
const BloggPage = lazy(() => import("./pages/v3/BloggPage"));
const BlogPostPage = lazy(() => import("./pages/v3/BlogPostPage"));
const OmOssPage = lazy(() => import("./pages/v3/OmOssPage"));
const KontaktPage = lazy(() => import("./pages/v3/KontaktPage"));
const VillkorPage = lazy(() => import("./pages/v3/VillkorPage"));
const IntegritetspolicyPage = lazy(() => import("./pages/v3/IntegritetspolicyPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={null}>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            
            {/* Landing page */}
            <Route path="/" element={<LandingPageV3 />} />

            {/* Redirects from old routes */}
            <Route path="/v1" element={<Navigate to="/" replace />} />
            <Route path="/lomoolo" element={<Navigate to="/" replace />} />
            <Route path="/pricing" element={<Navigate to="/priser" replace />} />
            <Route path="/privacy" element={<Navigate to="/integritetspolicy" replace />} />
            <Route path="/terms" element={<Navigate to="/villkor" replace />} />

            {/* V3 sub-pages */}
            <Route path="/priser" element={<PriserPage />} />
            <Route path="/funktioner" element={<FunktionerPage />} />
            <Route path="/sa-funkar-det" element={<SaFunkarDetPage />} />
            <Route path="/blogg" element={<BloggPage />} />
            <Route path="/blogg/:slug" element={<BlogPostPage />} />
            <Route path="/om-oss" element={<OmOssPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/villkor" element={<VillkorPage />} />
            <Route path="/integritetspolicy" element={<IntegritetspolicyPage />} />
            
            {/* App routes */}
            <Route path="/app/onboarding" element={<OnboardingPage />} />
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="calendar/social" element={<SocialCalendarPage />} />
              <Route path="calendar/newsletter" element={<NewsletterCalendarPage />} />
              <Route path="topics" element={<TopicsPage />} />
              <Route path="content" element={<ContentListPage />} />
              <Route path="content/:id" element={<ContentEditorPage />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="publish/queue" element={<PublishQueuePage />} />
              <Route path="publish/history" element={<PublishHistoryPage />} />
              <Route path="newsletter/subscribers" element={<SubscribersPage />} />
              <Route path="newsletter/editor/:id?" element={<NewsletterEditorPage />} />
              <Route path="admin/integrations" element={<IntegrationsPage />} />
              <Route path="admin/integrations/callback/:provider" element={<OAuthCallbackPage />} />
              <Route path="admin/team" element={<TeamPage />} />
              <Route path="admin/company" element={<CompanyProfilePage />} />
              <Route path="admin/account" element={<AccountSettingsPage />} />
            </Route>
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
